<template>
  <div>
    <div
      v-if="products.length === 0"
      class="h-48 lg:h-64"
    /> <!-- spacing until preview loads -->
    <div
      class="hidden md:grid grid-cols-1 md:grid-cols-3 sm:gap-x-8 md:gap-x-12 lg:grid-cols-3"
    >
      <collection-preview-product
        v-for="product in products.slice(0,3)"
        :key="product.id"
        :product="product"
      />
    </div>
    <div class="md:hidden">
      <swiper
        :modules="swiperModules"
        :slides-per-view="1.5"
        :centered-slides="true"
        virtual
        @swiper="onSwiper"
      >
        <swiper-slide
          v-for="(product, index) in swiperProducts"
          :key="index"
          :virtualindex="index"
        >
          <a
            :href="'/products/' + product.handle"
            class="block"
          >
            <img-yay
              ref="img"
              :src="product.variants[0].image.src"
              :width="400"
              :height="200"
              :lazy="false"
              sizes="(min-width: 640px) 458px, 98vw"
              alt="Product image"
              class="object-fit min w-full md:w-auto"
            />
            <div class="text-center">
              <h3 class="font-title leading-6 tracking-wide mb-0.5">
                <template v-if="splitTitle(product).length > 0">
                  <span class="inline-block">{{ splitTitle(product)[0] }}</span>
                  <span class="inline-block ml-1">{{ splitTitle(product)[1] }}</span>
                </template>
                <template v-else>
                  {{ product.title }}
                </template>
              </h3>
              <div class="font-title mt-1 text-sm">{{ strings.product.price.currency_symbol }}{{ formatPrice(product.price) }}</div>
            </div>
          </a>
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { toRefs, ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import has from 'lodash/has'
import { Virtual } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';

export default {
  components: {
    Swiper,
    SwiperSlide,
  },
  props: {
    collectionHandle: { type: String, required: true },
  },
  setup(props) {
    const { collectionHandle } = toRefs(props)
    const products = ref([])
    const swiperProducts = ref([]) // Needed to fake "loop mode"
    let productLimit = 7
    let swiper = {}
    const onSwiper = (s) => { swiper = s }
    // Resize often results in images disappearing,
    // so we use swiper.slideTo() to reduce the chance of this happening
    let width = window.innerWidth;
    const onWidthChange = () => {
      if (width != window.innerWidth) {
        width = window.innerWidth
        swiper.slideTo(productLimit -1, 0); swiper.slideTo(productLimit, 0)
      }
    }
    onMounted(() => window.addEventListener('resize', onWidthChange))
    onUnmounted(() => window.removeEventListener('resize', onWidthChange))

    const splitTitle = (product) => {
      const i = product.title.indexOf(': ')
      let result = []
      if (i > -1) {
        result = [product.title.slice(0, i+1), product.title.slice(i+1)]
      }
      return result
    }

    const populateProducts = (handle) => {
      axios.post("https://ahlem-eyewear.myshopify.com/api/2022-01/graphql.json",
        {
          query: `
            query CollectionProductsPreview($handle: String!, $productLimit: Int!){
              collectionByHandle(handle: $handle) {
                handle
                id
                products(first: $productLimit) {
                  edges {
                    node {
                      id
                      title
                      handle
                      images(first: 1) {
                        edges {
                          node {
                            id
                            src
                            altText
                          }
                        }
                      }
                      priceRange {
                        minVariantPrice {
                          amount
                          currencyCode
                        }
                      }
                      variants(first: 10) {
                        edges {
                          node {
                            id
                            title
                            image {
                              src
                            }
                            moreImages: metafield(
                              namespace: "custom_fields"
                              key: "variant_images"
                            ) {
                              value
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: { handle, productLimit }
        },
        {
          headers: {'X-Shopify-Storefront-Access-Token': '70c1a347a516396b3278c5e2a62c6302'},
        }
      ).then((result) => {
        if(has(result, 'data.errors')) {
          console.error("Errors getting collection preview products. See array below.");
          console.error(result.data.errors);
        }
        if(has(result, 'data.data.collectionByHandle.products')) {
          let finalResult = result.data.data.collectionByHandle.products.edges
            .map(p => p.node)
          finalResult = finalResult.map(p => {
              p.images = []
              p.variants = p.variants.edges.map(v => v.node)
              p.variants.forEach(v => {
                p.images[v.title] = v.image.src
                v.featured_image = v.image // This smells funky
                // Convert ID from global ID to old ID
                v.id = atob(v.id).replace(/gid:\/\/shopify\/ProductVariant\/([0-9]+)/, '$1')
              })
              if (p.variants[0]) {
                p.sneakPeak = p.variants[0].moreImages.value
                p.sneakPeak = JSON.parse(p.sneakPeak)[0].variant_images
              }
              p.price = p.priceRange.minVariantPrice.amount * 100

              return p
            })
          products.value = finalResult
          // We have to duplicate the related products because "Loop Mode" doesn't seem to be working
          // in Swiper 7 with Virtual Slides in Vue
          let val = products.value
          for(var i=0; i < 3; i++){
            val = val.concat(val)
          }
          swiperProducts.value = val
          nextTick().then(() => swiper.slideTo(7, 0))
        }
      })
    }

    watch(collectionHandle, (handle) => {
      populateProducts(handle)
    })
    populateProducts(collectionHandle.value)

    return {
      products,
      swiperProducts,
      swiperModules: [Virtual],
      onSwiper,
      splitTitle,
      strings: window.themeVariables.i18n
    }
  },
}
</script>
